import EncryptionService from './encryption'

/**
 * Système d'audit logging pour tracer toutes les actions importantes
 * Conforme aux exigences de sécurité et de conformité
 */
class AuditLogger {
  constructor() {
    this.logs = this.loadLogs()
    this.maxLogs = 1000 // Limiter le nombre de logs stockés localement
    this.severityLevels = {
      INFO: 1,
      WARNING: 2,
      ERROR: 3,
      CRITICAL: 4
    }
  }

  /**
   * Charge les logs depuis le stockage local
   * @returns {Array} - Liste des logs
   */
  loadLogs() {
    try {
      const stored = localStorage.getItem('audit_logs')
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('❌ Erreur lors du chargement des logs:', error)
      return []
    }
  }

  /**
   * Sauvegarde les logs dans le stockage local
   */
  saveLogs() {
    try {
      localStorage.setItem('audit_logs', JSON.stringify(this.logs))
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde des logs:', error)
    }
  }

  /**
   * Génère un ID unique pour le log
   * @returns {string} - ID du log
   */
  generateLogId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  /**
   * Obtient l'adresse IP du client (simulation)
   * En production, cela devrait venir du serveur
   * @returns {Promise<string>} - Adresse IP
   */
  async getClientIP() {
    try {
      // En production, remplacer par un vrai service d'IP
      return '127.0.0.1' // IP locale pour le développement
    } catch {
      return 'unknown'
    }
  }

  /**
   * Enregistre un événement d'audit
   * @param {Object} event - Événement à logger
   */
  async log(event) {
    try {
      const logEntry = {
        id: this.generateLogId(),
        timestamp: event.timestamp || new Date().toISOString(),
        action: event.action,
        user: event.user || 'anonymous',
        ip: event.ip || await this.getClientIP(),
        userAgent: navigator.userAgent,
        details: event.details || {},
        severity: event.severity || 'INFO',
        sessionId: event.sessionId || null,
        resource: event.resource || null,
        outcome: event.outcome || 'SUCCESS'
      }

      // Ajouter le log au début de la liste
      this.logs.unshift(logEntry)
      
      // Limiter le nombre de logs
      if (this.logs.length > this.maxLogs) {
        this.logs = this.logs.slice(0, this.maxLogs)
      }

      // Sauvegarder localement
      this.saveLogs()

      // En production, envoyer vers un service de logging
      if (import.meta.env.PROD) {
        await this.sendToLoggingService(logEntry)
      }

      // Log en console pour le développement
      if (import.meta.env.DEV) {
        console.log('📊 Audit Log:', logEntry)
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'enregistrement du log:', error)
    }
  }

  /**
   * Envoie les logs vers un service externe en production
   * @param {Object} logEntry - Entrée de log à envoyer
   */
  async sendToLoggingService(logEntry) {
    try {
      const loggingEndpoint = import.meta.env.VITE_LOGGING_ENDPOINT
      if (!loggingEndpoint) return

      await fetch(loggingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(logEntry)
      })
    } catch (error) {
      console.error('❌ Échec de l\'envoi du log vers le service:', error)
    }
  }

  /**
   * Récupère les logs avec filtres optionnels
   * @param {Object} filters - Filtres à appliquer
   * @returns {Array} - Logs filtrés
   */
  getLogs(filters = {}) {
    let filteredLogs = [...this.logs]

    // Filtre par action
    if (filters.action) {
      filteredLogs = filteredLogs.filter(log => log.action === filters.action)
    }

    // Filtre par utilisateur
    if (filters.user) {
      filteredLogs = filteredLogs.filter(log => log.user === filters.user)
    }

    // Filtre par niveau de sévérité
    if (filters.severity) {
      const minSeverity = this.severityLevels[filters.severity] || 1
      filteredLogs = filteredLogs.filter(log => 
        (this.severityLevels[log.severity] || 1) >= minSeverity
      )
    }

    // Filtre par date de début
    if (filters.dateFrom) {
      filteredLogs = filteredLogs.filter(log => 
        new Date(log.timestamp) >= new Date(filters.dateFrom)
      )
    }

    // Filtre par date de fin
    if (filters.dateTo) {
      filteredLogs = filteredLogs.filter(log => 
        new Date(log.timestamp) <= new Date(filters.dateTo)
      )
    }

    // Limiter le nombre de résultats
    if (filters.limit) {
      filteredLogs = filteredLogs.slice(0, filters.limit)
    }

    return filteredLogs
  }

  /**
   * Supprime les logs anciens
   * @param {number} daysToKeep - Nombre de jours à conserver
   */
  cleanupOldLogs(daysToKeep = 30) {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)

    this.logs = this.logs.filter(log => 
      new Date(log.timestamp) > cutoffDate
    )
    
    this.saveLogs()
  }

  /**
   * Exporte les logs au format JSON
   * @param {Object} filters - Filtres à appliquer
   * @returns {string} - Logs au format JSON
   */
  exportLogs(filters = {}) {
    const logs = this.getLogs(filters)
    return JSON.stringify(logs, null, 2)
  }

  /**
   * Statistiques des logs
   * @returns {Object} - Statistiques
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      byAction: {},
      bySeverity: {},
      byUser: {},
      last24h: 0
    }

    const last24h = new Date()
    last24h.setDate(last24h.getDate() - 1)

    this.logs.forEach(log => {
      // Par action
      stats.byAction[log.action] = (stats.byAction[log.action] || 0) + 1
      
      // Par sévérité
      stats.bySeverity[log.severity] = (stats.bySeverity[log.severity] || 0) + 1
      
      // Par utilisateur
      stats.byUser[log.user] = (stats.byUser[log.user] || 0) + 1
      
      // Dernières 24h
      if (new Date(log.timestamp) > last24h) {
        stats.last24h++
      }
    })

    return stats
  }

  /**
   * Actions d'audit prédéfinies pour faciliter l'utilisation
   */
  actions = {
    LOGIN_SUCCESS: 'Connexion réussie',
    LOGIN_FAILED: 'Tentative de connexion échouée',
    LOGOUT: 'Déconnexion',
    USER_CREATE: 'Création d\'utilisateur',
    USER_UPDATE: 'Modification d\'utilisateur',
    USER_DELETE: 'Suppression d\'utilisateur',
    DATA_EXPORT: 'Export de données',
    DATA_DELETE: 'Suppression de données',
    FILE_UPLOAD: 'Upload de fichier',
    FILE_DELETE: 'Suppression de fichier',
    BANNER_CREATE: 'Création de bannière',
    BANNER_UPDATE: 'Modification de bannière',
    BANNER_DELETE: 'Suppression de bannière',
    JOB_CREATE: 'Création d\'offre d\'emploi',
    JOB_UPDATE: 'Modification d\'offre d\'emploi',
    JOB_DELETE: 'Suppression d\'offre d\'emploi',
    APPLICATION_VIEW: 'Consultation de candidature',
    APPLICATION_UPDATE: 'Modification de candidature',
    SETTINGS_UPDATE: 'Modification des paramètres',
    SECURITY_VIOLATION: 'Violation de sécurité'
  }
}

// Export d'une instance singleton
export default new AuditLogger()
